// ─── src/components/sections/SlimHero.jsx ─────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { track } from "../../utils/track";
import { imgUrl } from "../../utils/imgUrl.js";
import { CATS, LOCATIONS } from "../../data/globalVendors";
import { getRegionSlugByName } from "../../data/geo";
import { resolveCountrySlug } from "../../utils/countrySlug";
import { SplitText } from "../ui/Animations";
import { useDesignSettings } from "../../hooks/useDesignSettings";
import { universalSearch, groupForSerp, groupForDiscovery, detectIntent, getPopularSearches, logSearch, logSearchClick } from "../../services/universalSearch";

// CATS ids now equal geo slugs (globalVendors.js derives CATS from geo.js VENDOR_CATEGORIES).
// No CAT_ID_TO_GEO needed, activeCat is used directly as the URL slug.

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

/* ── Clever-search location field (internal) ─────────────────────────────── */
/* Shows nothing on focus; as the user types, surfaces matching countries &
   cities in a compact ranked list, no huge mega menu. */
function LocationSearchField({ value, onChange, placeholder, items, ariaLabel, onEnter, containerRef }) {
  const [inputText, setInputText] = useState("");
  const [open, setOpen] = useState(false);
  const [hlIdx, setHlIdx] = useState(-1);
  const wrapRef = useRef(null);
  const fieldRef = useRef(null);
  const listRef = useRef(null);

  const q = inputText.toLowerCase().trim();

  // Build flat ranked results only when user is actually typing
  const results = (() => {
    if (!q) return [];
    const out = [];
    items.forEach((group) => {
      group.items.forEach((loc) => {
        const cLow = loc.country.toLowerCase();
        const countryStarts = cLow.startsWith(q);
        const countryIncludes = !countryStarts && cLow.includes(q);
        if (countryStarts || countryIncludes) {
          out.push({
            label: loc.country,
            value: loc.country,
            type: "country",
            region: group.group,
            rank: countryStarts ? 0 : 1,
          });
        }
        loc.cities.forEach((city) => {
          const ciLow = city.toLowerCase();
          const cityStarts = ciLow.startsWith(q);
          const cityIncludes = !cityStarts && ciLow.includes(q);
          if (cityStarts || cityIncludes) {
            out.push({
              label: city,
              value: `${city}, ${loc.country}`,
              type: "city",
              country: loc.country,
              region: group.group,
              rank: cityStarts ? 2 : 3,
            });
          }
        });
      });
    });
    out.sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
    return out.slice(0, 8);
  })();

  const hasResults = results.length > 0;

  useEffect(() => { setHlIdx(-1); }, [q]);

  // Click-outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        (!listRef.current || !listRef.current.contains(e.target))
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Scroll highlighted item
  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${hlIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hlIdx]);

  const selectItem = (item) => {
    onChange(item.value);
    setInputText(item.label);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open && q) setOpen(true);
      setHlIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHlIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hlIdx >= 0 && hlIdx < results.length) selectItem(results[hlIdx]);
      else if (onEnter) onEnter();
    } else if (e.key === "Escape") {
      setOpen(false);
      fieldRef.current?.blur();
    }
  };

  const displayValue = open
    ? inputText
    : value && value !== "all" && value !== "Worldwide" ? value : "";

  // Position dropdown to span full search bar width
  const getMegaPos = () => {
    if (!containerRef?.current || !wrapRef.current) return { left: 0, width: "100%" };
    const c = containerRef.current.getBoundingClientRect();
    const f = wrapRef.current.getBoundingClientRect();
    return { left: c.left - f.left, width: c.width };
  };
  const megaPos = open ? getMegaPos() : { left: 0, width: "100%" };

  // Highlight matching portion in label
  const highlight = (text) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q);
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span style={{ color: "#d39a21", fontWeight: 600 }}>{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div ref={wrapRef} className="home-hero-browse-field-wrap" style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        ref={fieldRef}
        value={displayValue}
        onChange={(e) => { setInputText(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setInputText(""); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={open && hasResults}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        style={{
          width: "100%",
          background: "none",
          border: "none",
          outline: "none",
          color: "#f5f0e8",
          fontSize: 14,
          fontFamily: NU,
          padding: "18px 0",
          minWidth: 0,
        }}
      />
      {open && q && (
        <div
          ref={listRef}
          role="listbox"
          className="home-hero-browse-dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: megaPos.left,
            width: megaPos.width,
            background: "rgba(12,11,8,0.82)",
            borderTop: "1.5px solid rgba(211,154,33,0.35)",
            border: "1px solid rgba(211,154,33,0.15)",
            borderRadius: 6,
            backdropFilter: "blur(32px) saturate(1.4)",
            WebkitBackdropFilter: "blur(32px) saturate(1.4)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 0.5px rgba(211,154,33,0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
            zIndex: 9999,
            textAlign: "left",
            padding: hasResults ? "8px 0" : "20px 24px",
          }}
        >
          {hasResults ? (
            results.map((item, idx) => {
              const isActive = idx === hlIdx;
              return (
                <div
                  key={`${item.value}-${idx}`}
                  data-idx={idx}
                  role="option"
                  aria-selected={isActive}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectItem(item)}
                  onMouseEnter={() => setHlIdx(idx)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "11px 18px",
                    cursor: "pointer",
                    fontFamily: NU,
                    fontSize: 13,
                    color: isActive ? "#d39a21" : "rgba(245,240,232,0.85)",
                    background: isActive ? "rgba(211,154,33,0.08)" : "transparent",
                    borderLeft: isActive ? "2px solid rgba(211,154,33,0.6)" : "2px solid transparent",
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={{ fontSize: 10, color: isActive ? "rgba(211,154,33,0.7)" : "rgba(211,154,33,0.35)", flexShrink: 0, transition: "color 0.15s" }}>
                      {item.type === "country" ? "◈" : "◎"}
                    </span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {highlight(item.label)}
                      {item.type === "city" && (
                        <span style={{ color: "rgba(245,240,232,0.3)", fontWeight: 400, marginLeft: 8, fontSize: 11, letterSpacing: "0.2px" }}>
                          {item.country}
                        </span>
                      )}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 8,
                    color: "rgba(211,154,33,0.22)",
                    letterSpacing: "2.5px",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    flexShrink: 0,
                    lineHeight: 1,
                  }}>
                    {item.region}
                  </span>
                </div>
              );
            })
          ) : (
            <div style={{ fontFamily: NU, fontSize: 12, color: "rgba(245,240,232,0.3)", textAlign: "center", fontWeight: 300 }}>
              No destinations match "{inputText}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── PredictiveField for categories (internal) ───────────────────────────── */
function PredictiveField({ value, onChange, placeholder, items, ariaLabel, onEnter, containerRef }) {
  const [inputText, setInputText] = useState("");
  const [open, setOpen] = useState(false);
  const [hlIdx, setHlIdx] = useState(-1);
  const wrapRef = useRef(null);
  const fieldRef = useRef(null);
  const listRef = useRef(null);

  const q = inputText.toLowerCase().trim();
  const filtered = items.filter((c) => !q || c.label.toLowerCase().includes(q));
  const flat = filtered.map((c) => ({ label: c.label, value: c.id, icon: c.icon }));

  useEffect(() => { setHlIdx(-1); }, [q]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        (!listRef.current || !listRef.current.contains(e.target))
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${hlIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hlIdx]);

  const selectItem = (item) => {
    onChange(item.value);
    setInputText(item.label);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHlIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHlIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hlIdx >= 0 && hlIdx < flat.length) selectItem(flat[hlIdx]);
      else if (onEnter) onEnter();
    } else if (e.key === "Escape") {
      setOpen(false);
      fieldRef.current?.blur();
    }
  };

  const displayValue = open
    ? inputText
    : value && value !== "all"
      ? (items.find((c) => c.id === value)?.label || "")
      : "";

  const getMegaPos = () => {
    if (!containerRef?.current || !wrapRef.current) return { left: 0, width: "100%" };
    const c = containerRef.current.getBoundingClientRect();
    const f = wrapRef.current.getBoundingClientRect();
    return { left: c.left - f.left, width: c.width };
  };
  const megaPos = open ? getMegaPos() : { left: 0, width: "100%" };
  const catCols = flat.length <= 4 ? 1 : flat.length <= 9 ? 2 : 3;

  return (
    <div ref={wrapRef} className="home-hero-browse-field-wrap" style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        ref={fieldRef}
        value={displayValue}
        onChange={(e) => { setInputText(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setInputText(""); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        style={{
          width: "100%",
          background: "none",
          border: "none",
          outline: "none",
          color: "#f5f0e8",
          fontSize: 14,
          fontFamily: NU,
          padding: "18px 0",
          minWidth: 0,
        }}
      />
      {open && flat.length > 0 && (
        <div
          ref={listRef}
          role="listbox"
          className="home-hero-browse-dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: megaPos.left,
            width: megaPos.width,
            background: "rgba(12,11,8,0.82)",
            borderTop: "1.5px solid rgba(211,154,33,0.35)",
            border: "1px solid rgba(211,154,33,0.15)",
            borderRadius: 6,
            backdropFilter: "blur(32px) saturate(1.4)",
            WebkitBackdropFilter: "blur(32px) saturate(1.4)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 0.5px rgba(211,154,33,0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
            zIndex: 9999,
            textAlign: "left",
          }}
        >
          <div
            className="home-hero-browse-cat-grid"
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${catCols}, 1fr)`,
              gap: 0,
              padding: "10px 6px",
            }}
          >
            {flat.map((item, idx) => (
              <div
                key={item.value}
                data-idx={idx}
                role="option"
                aria-selected={idx === hlIdx}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectItem(item)}
                onMouseEnter={() => setHlIdx(idx)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontFamily: NU,
                  fontSize: 12,
                  color: idx === hlIdx ? "#d39a21" : "rgba(245,240,232,0.85)",
                  background: idx === hlIdx ? "rgba(211,154,33,0.08)" : "transparent",
                  borderRadius: "var(--lwd-radius-input)",
                  transition: "background 0.12s, color 0.12s",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 13, color: "rgba(211,154,33,0.45)", width: 16, textAlign: "center", flexShrink: 0 }}>
                  {item.icon}
                </span>
                {item.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Universal search field (Field 1) ────────────────────────────────────────
   Standard business search, NOT Aura. Types resolve to three intents in one
   dropdown: category quick-picks, live federated results (venues, vendors,
   destinations, guides, editorial) from the server-side `universal_search` RPC,
   and a "search all" row → /search?q=. Clicking a result routes directly. */
function UniversalSearchField({
  value, onChange, placeholder, items, ariaLabel, onEnter, containerRef,
  onQueryChange, onPickVenue, onPickDestination, onPickEdit, onSubmitSearch,
}) {
  const [inputText, setInputText] = useState("");
  const [open, setOpen] = useState(false);
  const [hlIdx, setHlIdx] = useState(-1);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const fieldRef = useRef(null);
  const listRef = useRef(null);
  const reqRef = useRef(0);

  const q = inputText.trim();
  const qLow = q.toLowerCase();

  // Category quick-picks from the static CATS list ("Browse all Venues")
  const catPicks = (!q ? [] : items.filter((c) => c.label.toLowerCase().includes(qLow)))
    .slice(0, 4)
    .map((c) => ({ kind: "category", id: c.id, label: c.label, icon: c.icon }));

  // Debounced server-side search, stale-guarded
  useEffect(() => {
    if (q.length < 2) { setGroups([]); setLoading(false); return; }
    setLoading(true);
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      const { grouped, results } = await universalSearch(q, { limit: 18 });
      if (id !== reqRef.current) return;
      setGroups(grouped);
      setLoading(false);
      logSearch({ query: q, resultCount: results.length, source: "homepage" });
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  // Lift the query up so the gold Search button can route free-text → SERP
  useEffect(() => { onQueryChange?.(q); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setHlIdx(-1); }, [q]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          (!listRef.current || !listRef.current.contains(e.target))) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${hlIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hlIdx]);

  // Flat, keyboard-navigable list: search-all → categories → result rows
  const flat = [];
  if (q.length >= 2) flat.push({ kind: "searchall", label: q });
  catPicks.forEach((c) => flat.push(c));
  groups.forEach((g) => g.items.forEach((row) => flat.push({ kind: "result", row })));

  const topResult = groups[0]?.items?.[0] || null;

  const goResult = (r) => {
    setOpen(false);
    setInputText("");
    if (r.route.kind === "venue") onPickVenue?.(r.route.payload);
    else if (r.route.kind === "destination") onPickDestination?.(r.route);
    else onPickEdit?.(r.route);
  };

  // Free-text submit. Prefer the SERP when wired; otherwise route to the best
  // match so a search is never a dead end (and never falls back to Aura).
  const doSubmit = () => {
    if (q.length < 2) return;
    if (onSubmitSearch) { setOpen(false); onSubmitSearch(q); return; }
    if (topResult) { logSearchClick({ query: q, result: topResult, source: "homepage" }); goResult(topResult); }
  };

  const act = (entry) => {
    if (!entry) return;
    if (entry.kind === "category") { onChange(entry.id); setInputText(entry.label); setOpen(false); onEnter?.(); return; }
    if (entry.kind === "searchall") { doSubmit(); return; }
    logSearchClick({ query: q, result: entry.row, source: "homepage" });
    goResult(entry.row);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHlIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHlIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (hlIdx >= 0 && hlIdx < flat.length) return act(flat[hlIdx]);
      // No selection: one exact match → straight there; else → submit (SERP / best match)
      if (topResult && Number(topResult.score) >= 1000) {
        logSearchClick({ query: q, result: topResult, source: "homepage" });
        goResult(topResult);
      } else {
        doSubmit();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      fieldRef.current?.blur();
    }
  };

  const displayValue = open
    ? inputText
    : value && value !== "all" ? (items.find((c) => c.id === value)?.label || "") : "";

  const getMegaPos = () => {
    if (!containerRef?.current || !wrapRef.current) return { left: 0, width: "100%" };
    const c = containerRef.current.getBoundingClientRect();
    const f = wrapRef.current.getBoundingClientRect();
    return { left: c.left - f.left, width: c.width };
  };
  const megaPos = open ? getMegaPos() : { left: 0, width: "100%" };

  const showPanel = open && q.length >= 1;
  const idxOf = (entry) => flat.indexOf(entry);

  return (
    <div ref={wrapRef} className="home-hero-browse-field-wrap" style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        ref={fieldRef}
        value={displayValue}
        onChange={(e) => { setInputText(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setInputText(""); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={showPanel}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        style={{ width: "100%", background: "none", border: "none", outline: "none", color: "#f5f0e8", fontSize: 14, fontFamily: NU, padding: "18px 0", minWidth: 0 }}
      />
      {showPanel && (
        <div
          ref={listRef}
          role="listbox"
          className="home-hero-browse-dropdown"
          style={{
            position: "absolute", top: "calc(100% + 8px)", left: megaPos.left, width: megaPos.width,
            maxHeight: 420, overflowY: "auto",
            background: "rgba(12,11,8,0.9)", borderTop: "1.5px solid rgba(211,154,33,0.35)",
            border: "1px solid rgba(211,154,33,0.15)", borderRadius: 6,
            backdropFilter: "blur(32px) saturate(1.4)", WebkitBackdropFilter: "blur(32px) saturate(1.4)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 0 0 0.5px rgba(211,154,33,0.12), inset 0 1px 0 rgba(255,255,255,0.04)",
            zIndex: 9999, textAlign: "left", padding: "6px 0",
          }}
        >
          {/* Search-all row */}
          {q.length >= 2 && (() => { const i = idxOf(flat.find((f) => f.kind === "searchall")); return (
            <div
              data-idx={i} role="option" aria-selected={i === hlIdx}
              onMouseDown={(e) => e.preventDefault()} onClick={() => act({ kind: "searchall", label: q })} onMouseEnter={() => setHlIdx(i)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", fontFamily: NU, fontSize: 13, color: i === hlIdx ? "#d39a21" : "rgba(245,240,232,0.92)", background: i === hlIdx ? "rgba(211,154,33,0.08)" : "transparent" }}
            >
              <span style={{ color: "rgba(211,154,33,0.6)", width: 16, textAlign: "center" }}>⌕</span>
              Search for “<strong style={{ fontWeight: 600 }}>{q}</strong>”
            </div>
          ); })()}

          {/* Category quick-picks */}
          {catPicks.length > 0 && (
            <div style={{ padding: "4px 0 2px" }}>
              <div style={{ padding: "6px 16px 4px", fontFamily: NU, fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(211,154,33,0.6)" }}>Browse</div>
              {catPicks.map((c) => { const i = idxOf(c); return (
                <div key={`cat-${c.id}`} data-idx={i} role="option" aria-selected={i === hlIdx}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => act(c)} onMouseEnter={() => setHlIdx(i)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", cursor: "pointer", fontFamily: NU, fontSize: 12.5, color: i === hlIdx ? "#d39a21" : "rgba(245,240,232,0.85)", background: i === hlIdx ? "rgba(211,154,33,0.08)" : "transparent" }}>
                  <span style={{ color: "rgba(211,154,33,0.45)", width: 16, textAlign: "center" }}>{c.icon}</span>
                  Browse all {c.label}
                </div>
              ); })}
            </div>
          )}

          {/* Federated result groups */}
          {groups.map((g) => (
            <div key={g.key} style={{ padding: "4px 0 2px" }}>
              <div style={{ padding: "6px 16px 4px", fontFamily: NU, fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(211,154,33,0.6)" }}>{g.title}</div>
              {g.items.map((row) => { const entry = { kind: "result", row }; const i = idxOf(flat.find((f) => f.kind === "result" && f.row === row)); return (
                <div key={`${row.entity}-${row.id}`} data-idx={i} role="option" aria-selected={i === hlIdx}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => act(entry)} onMouseEnter={() => setHlIdx(i)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px", cursor: "pointer", background: i === hlIdx ? "rgba(211,154,33,0.08)" : "transparent" }}>
                  <span style={{ width: 40, height: 30, flexShrink: 0, borderRadius: 4, overflow: "hidden", background: "rgba(211,154,33,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {row.image
                      ? <img src={row.image} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ color: "rgba(211,154,33,0.5)", fontSize: 13 }}>{row.entity === "destination" ? "◎" : "✦"}</span>}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "block", fontFamily: GD, fontSize: 14, color: i === hlIdx ? "#f3d79a" : "#f5f0e8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.title}</span>
                    <span style={{ display: "block", fontFamily: NU, fontSize: 11, color: "rgba(245,240,232,0.5)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.subtitle}</span>
                  </span>
                </div>
              ); })}
            </div>
          ))}

          {/* Loading / empty states, never a dead end */}
          {loading && groups.length === 0 && (
            <div style={{ padding: "10px 16px", fontFamily: NU, fontSize: 12, color: "rgba(245,240,232,0.5)" }}>Searching…</div>
          )}
          {!loading && q.length >= 2 && groups.length === 0 && catPicks.length === 0 && (
            <div style={{ padding: "10px 16px", fontFamily: NU, fontSize: 12, color: "rgba(245,240,232,0.5)" }}>
              No direct matches, press Enter to search everything.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── DiscoveryBar (the homepage front door) ───────────────────────────────────
   ONE command bar, no AI/Browse toggle. Instant federated results from the
   shipped universal_search; the exact match is promoted to an "Open …" action;
   Aura rises as a calm concierge row (auto-highlighted) only when the query
   reads like a sentence, never a silent gamble, never a dead end. Surface only;
   the engines underneath (Universal Search + Aura) are unchanged. */
const POPULAR_SEARCHES = ["Lake Como", "Tuscany", "Amalfi Coast", "Photographers"];
const NL_RE = /\b(need|want|looking|find me|help me|budget|around|under|over|guests?|people|pax|near|with a|that has|in (?:january|february|march|april|may|june|july|august|september|october|november|december|spring|summer|autumn|winter))\b/i;
function isNaturalLanguageQuery(q) {
  const t = (q || "").trim();
  return t.split(/\s+/).length >= 6 || NL_RE.test(t);
}
function openVerb(entity) {
  if (entity === "destination") return "Explore";
  if (entity === "atlas") return "Open the guide to";
  if (entity === "publication") return "Read";
  if (entity === "real_wedding") return "See";
  if (entity === "guide" || entity === "editorial") return "Read";
  return "Open";
}
function rowGlyph(entity) {
  if (entity === "destination") return "◎";
  if (entity === "atlas") return "✣";
  if (entity === "publication") return "❖";
  if (entity === "real_wedding") return "♥";
  return "✦";
}

function DiscoveryBar({ containerRef, onViewVenue, onViewRegion, onViewEditArticle, onViewAtlas, onViewPublication, onViewRealWedding, onViewSearch, onOpenImmersive }) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hlIdx, setHlIdx] = useState(0);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popular, setPopular] = useState(POPULAR_SEARCHES);
  const wrapRef = useRef(null);
  const fieldRef = useRef(null);
  const listRef = useRef(null);
  const reqRef = useRef(0);

  const q = input.trim();
  const nl = isNaturalLanguageQuery(q);

  useEffect(() => { getPopularSearches({ limit: 4 }).then(setPopular); }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      const { results: rs } = await universalSearch(q, { limit: 18 });
      if (id !== reqRef.current) return;
      setResults(rs);
      setLoading(false);
      logSearch({ query: q, resultCount: rs.length, source: "homepage" });
    }, 170);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setHlIdx(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target) && (!listRef.current || !listRef.current.contains(e.target))) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useEffect(() => {
    if (hlIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${hlIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [hlIdx]);

  // Entity mode (a specific named thing → Spotlight lead) vs Discovery mode
  // (a place/topic → the whole universe, intent-ordered). Destinations are
  // places, so they never "promote"; knowledge queries ("getting married in…")
  // are explorations, so they open the Atlas-led universe rather than promote a
  // single exact-title article.
  const promoted = (!nl && detectIntent(q) !== "knowledge" && results[0] && results[0].score >= 800 && results[0].entity !== "destination") ? results[0] : null;
  const groups = promoted ? [] : groupForDiscovery(results, q);
  const rest = promoted ? results.slice(1, 5) : [];
  const isUniverse = !promoted && groups.length >= 2;
  const showAura = !!onOpenImmersive && q.length >= 2 && (nl || isUniverse || (!loading && results.length === 0));

  const flat = [];
  if (showAura) flat.push({ kind: "aura" });
  if (promoted) { flat.push({ kind: "open", row: promoted }); rest.forEach((r) => flat.push({ kind: "result", row: r })); }
  else { groups.forEach((g) => g.items.forEach((row) => flat.push({ kind: "result", row }))); }
  // "See all results" → full SERP for the query (e.g. every planner in Italy, not just the top 3 shown).
  const canSeeAll = q.length >= 2 && !!onViewSearch && (promoted || groups.some((g) => g.items.length));
  if (canSeeAll) flat.push({ kind: "seeall" });

  const goRow = (r, src) => {
    logSearchClick({ query: q, result: r, source: src || "homepage" });
    setOpen(false); setInput("");
    const k = r.route.kind;
    if (k === "venue") onViewVenue?.(r.route.payload);
    else if (k === "destination") onViewRegion?.(r.route.countrySlug, r.route.regionSlug);
    else if (k === "atlas") onViewAtlas?.(r.route.slug);
    else if (k === "publication") onViewPublication?.(r.route.slug);
    else if (k === "realwedding") onViewRealWedding?.(r.route.slug);
    else onViewEditArticle?.(r.route.slug);
  };
  const act = (e) => {
    if (!e) return;
    if (e.kind === "aura") { onOpenImmersive?.(q); setOpen(false); return; }
    if (e.kind === "seeall") { setOpen(false); setInput(""); onViewSearch?.(q); return; }
    goRow(e.row, e.kind === "open" ? "homepage-open" : "homepage");
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) { setOpen(true); return; } setHlIdx((i) => Math.min(i + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHlIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (flat[hlIdx]) return act(flat[hlIdx]); if (q.length >= 2) { onViewSearch ? onViewSearch(q) : onOpenImmersive?.(q); } }
    else if (e.key === "Escape") { setOpen(false); fieldRef.current?.blur(); }
  };

  const idxFor = (row) => flat.findIndex((f) => (f.kind === "result" || f.kind === "open") && f.row === row);
  const showDrop = open && q.length >= 1;

  const Thumb = ({ r, big }) => (
    <span style={{ width: big ? 62 : 42, height: big ? 50 : 32, flexShrink: 0, borderRadius: big ? 7 : 5, overflow: "hidden", background: "rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(211,154,33,0.55)", fontSize: big ? 20 : 15 }}>
      {r.image ? <img src={r.image} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : rowGlyph(r.entity)}
    </span>
  );
  // Quieter "similar" rows, visually ~70% of the promoted result.
  const ResultRow = (r) => { const i = idxFor(r); return (
    <div key={`${r.entity}-${r.id}`} className="lwd-row" data-idx={i} role="option" aria-selected={i === hlIdx}
      onMouseDown={(e) => e.preventDefault()} onClick={() => act(flat[i])} onMouseEnter={() => setHlIdx(i)}
      style={{ display: "flex", alignItems: "center", gap: 13, padding: "10px 24px", cursor: "pointer", textAlign: "left", background: i === hlIdx ? "rgba(211,154,33,0.08)" : "transparent", animationDelay: `${Math.min(i, 8) * 45}ms` }}>
      <Thumb r={r} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", fontFamily: GD, fontSize: 15.5, color: "rgba(245,240,232,0.88)", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title}</span>
        <span style={{ display: "block", fontFamily: NU, fontSize: 11, color: "rgba(245,240,232,0.42)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.subtitle}</span>
      </span>
    </div>
  ); };
  const SEC_GLYPH = { atlas: "✣", publications: "❖", realweddings: "♥" };
  const sec = (t, glyph) => <div style={{ fontFamily: NU, fontSize: 9.5, letterSpacing: "0.3em", textTransform: "uppercase", color: "rgba(211,154,33,0.65)", padding: "12px 24px 6px", display: "flex", alignItems: "center", gap: 8 }}>{glyph && <span style={{ fontSize: 11, color: "rgba(211,154,33,0.85)", letterSpacing: 0 }}>{glyph}</span>}{t}</div>;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%", maxWidth: 720, margin: "0 auto", textAlign: "left" }}>
      <style>{`
        @keyframes lwdCaret { 0%,100% { opacity: 1 } 50% { opacity: 0.18 } }
        @keyframes lwdRowRise { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: none } }
        .lwd-row, .lwd-aura, .lwd-open { animation: lwdRowRise 180ms ease both; }
        .lwd-bar-glass { backdrop-filter: blur(20px) saturate(1.1); -webkit-backdrop-filter: blur(20px) saturate(1.1); transition: backdrop-filter 200ms ease, background 200ms ease, border-color 200ms ease, box-shadow 200ms ease, border-radius 150ms ease; }
        .lwd-bar-glass.lwd-focus { backdrop-filter: blur(23px) saturate(1.16); -webkit-backdrop-filter: blur(23px) saturate(1.16); }
        .lwd-drop-glass { backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); }
        @media (max-width: 640px) {
          .lwd-bar-glass { backdrop-filter: blur(16px) saturate(1.1); -webkit-backdrop-filter: blur(16px) saturate(1.1); }
          .lwd-bar-glass.lwd-focus { backdrop-filter: blur(18px) saturate(1.16); -webkit-backdrop-filter: blur(18px) saturate(1.16); }
          .lwd-drop-glass { backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px); }
        }
      `}</style>
      <div ref={containerRef} className={`lwd-bar-glass${focused ? " lwd-focus" : ""}`}
        style={{ position: "relative", display: "flex", alignItems: "center", gap: 16,
          padding: "clamp(18px, 3.3vw, 26px) clamp(18px, 3.3vw, 26px)",
          background: focused
            ? "linear-gradient(to bottom, rgba(40,35,27,0.32) 0%, rgba(23,19,14,0.42) 55%, rgba(11,9,6,0.52) 100%)"
            : "linear-gradient(to bottom, rgba(31,27,21,0.28) 0%, rgba(18,15,11,0.38) 55%, rgba(9,7,5,0.48) 100%)",
          border: `1px solid ${focused ? "rgba(211,154,33,0.52)" : "rgba(211,154,33,0.3)"}`,
          borderRadius: showDrop ? "16px 16px 0 0" : 16,
          boxShadow: focused
            ? "inset 0 1px 0 rgba(255,255,255,0.12), inset 0 0 34px rgba(0,0,0,0.24), 0 0 0 1px rgba(211,154,33,0.14), 0 0 28px rgba(211,154,33,0.1), 0 22px 54px rgba(0,0,0,0.48)"
            : "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 32px rgba(0,0,0,0.24), 0 20px 50px rgba(0,0,0,0.46)" }}>
        <span aria-hidden="true" style={{ color: "#d39a21", fontSize: 27, flexShrink: 0, opacity: focused ? 1 : 0.9, transition: "opacity 200ms ease" }}>⌕</span>
        <div style={{ position: "relative", flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
          {focused && !input && (
            <span aria-hidden="true" style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", width: 2, height: 24, background: "#d39a21", borderRadius: 2, animation: "lwdCaret 1.9s ease-in-out infinite" }} />
          )}
          {!input && (
            <div aria-hidden="true" style={{ position: "absolute", top: "50%", left: focused ? 12 : 0, transform: "translateY(-50%)", pointerEvents: "none", transition: "left 200ms ease" }}>
              <div style={{ fontFamily: GD, fontSize: 20, color: "rgba(248,241,226,0.95)", lineHeight: 1.15, textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>Search venues, destinations or creatives</div>
              <div style={{ fontFamily: NU, fontSize: 12.5, color: "rgba(248,241,226,0.7)", marginTop: 2, textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>or simply describe your wedding</div>
            </div>
          )}
          <input ref={fieldRef} value={input}
            onChange={(e) => { setInput(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => { setFocused(true); setOpen(true); }} onBlur={() => setFocused(false)} onKeyDown={onKey}
            aria-label="Search venues, destinations, creatives, or describe your wedding"
            role="combobox" aria-expanded={showDrop} aria-autocomplete="list"
            style={{ width: "100%", background: "none", border: "none", outline: "none", color: "#f8f1e2", fontFamily: GD, fontSize: 21, padding: "4px 0", minWidth: 0, caretColor: input ? "#d39a21" : "transparent", textShadow: "0 1px 3px rgba(0,0,0,0.32)" }} />
        </div>
      </div>

      {showDrop && (
        <div ref={listRef} role="listbox" className="lwd-drop-glass"
          style={{ position: "absolute", top: "100%", left: 0, width: "100%", maxHeight: "min(440px, 60vh)", overflowY: "auto",
            background: "rgba(52,53,58,0.95)", border: "1px solid rgba(211,154,33,0.16)", borderTop: "1.5px solid rgba(211,154,33,0.38)", borderRadius: "0 0 16px 16px",
            boxShadow: "0 28px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)", zIndex: 9999, padding: "15px 0 12px" }}>

          {showAura && (() => { const i = flat.findIndex((f) => f.kind === "aura"); return (
            <div className="lwd-aura" data-idx={i} role="option" aria-selected={i === hlIdx}
              onMouseDown={(e) => e.preventDefault()} onClick={() => act({ kind: "aura" })} onMouseEnter={() => setHlIdx(i)}
              style={{ display: "flex", alignItems: "center", gap: 17, padding: "20px 24px", cursor: "pointer",
                background: i === hlIdx
                  ? "linear-gradient(95deg, rgba(211,154,33,0.26), rgba(211,154,33,0.14) 70%)"
                  : "linear-gradient(95deg, rgba(211,154,33,0.18), rgba(211,154,33,0.09) 70%)",
                borderBottom: "1px solid rgba(211,154,33,0.22)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)", transition: "background 150ms ease" }}>
              <span style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, background: "rgba(211,154,33,0.22)", border: "1px solid rgba(211,154,33,0.5)", boxShadow: "0 0 20px rgba(211,154,33,0.35)", display: "flex", alignItems: "center", justifyContent: "center", color: "#f3d79a", fontSize: 22 }}>✦</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontFamily: GD, fontSize: 19, color: "#f8e2ad", fontWeight: 500 }}>Explore with Aura</span>
                <span style={{ display: "block", fontFamily: NU, fontSize: 12.5, color: "rgba(248,241,226,0.78)", marginTop: 3, lineHeight: 1.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Find venues, creatives and destinations matched to your style, guest count and vision.</span>
                <span style={{ display: "block", fontFamily: NU, fontSize: 11.5, color: "rgba(245,240,232,0.45)", fontStyle: "italic", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>“{q}”</span>
              </span>
            </div>
          ); })()}

          {promoted && (() => { const i = flat.findIndex((f) => f.kind === "open"); return (
            <div className="lwd-open" data-idx={i} role="option" aria-selected={i === hlIdx}
              onMouseDown={(e) => e.preventDefault()} onClick={() => act(flat[i])} onMouseEnter={() => setHlIdx(i)}
              style={{ display: "flex", alignItems: "center", gap: 18, padding: "20px 24px 20px 21px", cursor: "pointer", borderLeft: "3px solid #d39a21", background: i === hlIdx ? "rgba(211,154,33,0.24)" : "rgba(211,154,33,0.16)", transition: "background 150ms ease" }}>
              <Thumb r={promoted} big />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontFamily: GD, fontSize: 23, color: "#f8e2ad", fontWeight: 500, lineHeight: 1.12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{openVerb(promoted.entity)} {promoted.title}</span>
                <span style={{ display: "block", fontFamily: NU, fontSize: 12.5, color: "rgba(245,240,232,0.66)", marginTop: 3 }}>{promoted.subtitle}</span>
              </span>
            </div>
          ); })()}

          {promoted && rest.length > 0 && <><div style={{ height: 1, background: "rgba(211,154,33,0.16)", margin: "10px 0 2px" }} />{sec("You may also like")}{rest.map(ResultRow)}</>}
          {!promoted && groups.map((g) => <div key={g.key}>{sec(g.title, SEC_GLYPH[g.key])}{g.items.map(ResultRow)}</div>)}

          {canSeeAll && (() => { const i = flat.findIndex((f) => f.kind === "seeall"); return (
            <div data-idx={i} role="option" aria-selected={i === hlIdx}
              onMouseDown={(e) => e.preventDefault()} onClick={() => act({ kind: "seeall" })} onMouseEnter={() => setHlIdx(i)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 24px", marginTop: 6, cursor: "pointer",
                borderTop: "1px solid rgba(211,154,33,0.16)", background: i === hlIdx ? "rgba(211,154,33,0.12)" : "transparent", transition: "background 150ms ease" }}>
              <span style={{ fontFamily: NU, fontSize: 12.5, letterSpacing: "0.04em", color: "#f3d79a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>See all results for “{q}”</span>
              <span aria-hidden style={{ color: "#d39a21", fontSize: 15, flexShrink: 0 }}>→</span>
            </div>
          ); })()}

          {loading && results.length === 0 && <div style={{ padding: "14px 24px", fontFamily: NU, fontSize: 12.5, color: "rgba(245,240,232,0.5)" }}>Searching…</div>}
          {!loading && q.length >= 2 && results.length === 0 && <div style={{ padding: "12px 24px 4px", fontFamily: NU, fontSize: 12.5, color: "rgba(245,240,232,0.5)" }}>No exact matches, let Aura help, or press Enter to explore.</div>}
        </div>
      )}

      {!input && (
        <div style={{ display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap", marginTop: 22, justifyContent: "center" }}>
          <span style={{ fontFamily: NU, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(245,240,232,0.5)" }}>Popular searches</span>
          {popular.map((p) => (
            <button key={p} onClick={() => { setInput(p); setOpen(true); fieldRef.current?.focus(); }}
              style={{ fontFamily: GD, fontSize: 15, color: "#f5f0e8", background: "none", border: "0.5px solid rgba(211,154,33,0.2)", borderRadius: 999, padding: "5px 16px", cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(211,154,33,0.55)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(211,154,33,0.2)")}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Utility functions ───────────────────────────────────────────────────── */
function extractYouTubeId(url) {
  if (!url) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return '';
}

function extractVimeoId(url) {
  if (!url) return '';
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : '';
}

/* ── SlimHero ────────────────────────────────────────────────────────────── */
export default function SlimHero({
  venues = [],
  backgroundData = null,
  onViewRegion,
  onViewRegionCategory,
  onViewCategory,
  onViewCountry,
  onViewVenue,
  onViewEditArticle,
  onViewAtlas,
  onViewPublication,
  onViewRealWedding,
  onViewSearch,
  onOpenImmersive,
  // Optional copy overrides, H7 (and any future variant) can replace
  // the eyebrow + the two-line headline without altering the live
  // homepage. Defaults preserve the previous copy so every other page
  // using SlimHero is unaffected.
  eyebrow,
  headlineLine1,
  headlineLine2,
  supportLine,
}) {
  const C = useTheme();
  const designSettings = useDesignSettings();

  // Use ordered slide list if saved by HeroSliderManager; otherwise fall back
  // to combining homepage_slider_image + default_hero_slider_1..4.
  const heroSliderImages = designSettings.homepage_hero_slides_parsed?.length
    ? designSettings.homepage_hero_slides_parsed
    : [
        designSettings.homepage_slider_image,
        ...(designSettings.default_hero_slider || []),
      ].filter(Boolean);
  const heroSliderKey = heroSliderImages.join("|");

  const [idx, setIdx] = useState(0);
  const [query, setQuery] = useState("");
  const [searchMode, setSearchMode] = useState("ai");
  const [activeCat, setActiveCat] = useState("all");
  const [activeCountry, setActiveCountry] = useState("Worldwide");
  const [browseQuery, setBrowseQuery] = useState("");
  const [parallaxY, setParallaxY] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [browseHint, setBrowseHint] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const hintTimerRef = useRef(null);
  const inputRef = useRef(null);
  const searchBarRef = useRef(null);
  const sectionRef = useRef(null);
  // Tracks the slide that just left so it can remain rendered during the
  // 2.5s crossfade-out. Using a ref avoids triggering an extra render.
  const prevIdxRef = useRef(-1);

  // Trigger text-split after mount
  useEffect(() => { const t = setTimeout(() => setHeroLoaded(true), 150); return () => clearTimeout(t); }, []);

  // Defer background video loading, lets critical content paint before YouTube/Vimeo JS loads
  useEffect(() => { const t = setTimeout(() => setVideoReady(true), 1200); return () => clearTimeout(t); }, []);

  // Auto-advance background image
  // Priority: Page Studio backgroundData → admin hero slider images → dark bg
  useEffect(() => {
    const images = backgroundData?.backgroundImages || heroSliderImages;
    if (!images?.length) return;
    const t = setInterval(() => setIdx((i) => {
      prevIdxRef.current = i;
      return (i + 1) % images.length;
    }), 7000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundData?.backgroundImages, heroSliderKey]);

  // Subtle parallax, image drifts at 15 % of scroll speed
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.bottom > 0 && rect.top < vh) {
        const progress = (vh - rect.top) / (vh + rect.height);
        setParallaxY((progress - 0.5) * rect.height * 0.15);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAuraSearch = () => {
    track("search_submit", { query, mode: "ai" });
    if (onOpenImmersive) {
      onOpenImmersive(query.trim() || null);
      setQuery("");
    } else {
      // Fallback to mini bar if no immersive handler wired
      window.dispatchEvent(
        new CustomEvent("lwd:openAura", { detail: { query: query.trim() } })
      );
      setQuery("");
    }
  };

  const handleBrowseSearch = () => {
    track("search_submit", { mode: "browse", cat: activeCat, country: activeCountry });

    let countrySlug = null, regionSlug = null;
    if (activeCountry && activeCountry !== "Worldwide") {
      const comma = activeCountry.lastIndexOf(", ");
      if (comma > 0) {
        const cityName    = activeCountry.slice(0, comma);
        const countryName = activeCountry.slice(comma + 2);
        countrySlug = resolveCountrySlug(countryName);
        const rSlug = getRegionSlugByName(cityName);
        regionSlug  = rSlug !== "all" ? rSlug : null;
      } else {
        countrySlug = resolveCountrySlug(activeCountry);
      }
    }

    const catSlug = activeCat && activeCat !== "all" ? activeCat : null;

    if (countrySlug && regionSlug && catSlug) {
      onViewRegionCategory?.(countrySlug, regionSlug, catSlug);   // /italy/tuscany/wedding-venues
    } else if (countrySlug && catSlug) {
      onViewRegionCategory?.(countrySlug, null, catSlug);          // /italy/wedding-venues  ← fixed
    } else if (countrySlug && regionSlug) {
      onViewRegion?.(countrySlug, regionSlug);                     // /italy/tuscany
    } else if (catSlug) {
      onViewCategory?.({ category: catSlug });                     // /wedding-venues
    } else if (countrySlug) {
      if (onViewCountry) {
        onViewCountry(countrySlug);                                // /italy → LocationPage
      } else {
        document.getElementById("vendor-preview")?.scrollIntoView({ behavior: "smooth" });
      }
    } else if (browseQuery.trim().length >= 2) {
      // Free-text with no category/destination selected → universal search.
      handleUniversalSubmit(browseQuery.trim());
    } else {
      document.getElementById("vendor-preview")?.scrollIntoView({ behavior: "smooth" });
      clearTimeout(hintTimerRef.current);
      setBrowseHint(true);
      hintTimerRef.current = setTimeout(() => setBrowseHint(false), 3000);
    }
  };

  // Free-text submit → /search?q= when wired; otherwise best-match via the RPC
  // (never a dead end, and never falls back to Aura).
  const handleUniversalSubmit = (text) => {
    track("search_submit", { mode: "browse-name", q: text });
    if (onViewSearch) { onViewSearch(text); return; }
    universalSearch(text, { limit: 1 }).then(({ results }) => {
      const r = results[0];
      if (!r) return;
      if (r.route.kind === "venue") onViewVenue?.(r.route.payload);
      else if (r.route.kind === "destination") onViewRegion?.(r.route.countrySlug, r.route.regionSlug);
      else onViewEditArticle?.(r.route.slug);
    });
  };

  const browseCategories = CATS.filter((c) => c.id !== "all");

  return (
    <section
      ref={sectionRef}
      aria-label="Hero"
      className="home-hero"
      style={{
        position: "relative",
        height: "100svh",
        minHeight: 700,
        background: "#040302",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexDirection: "column",
        transition: "height 0.6s ease",
      }}
    >
      {/* Ken Burns + slide animation keyframes */}
      <style>{`
        @keyframes kbZoomIn  { from { transform: scale(1.0);                      } to { transform: scale(1.10);                    } }
        @keyframes kbZoomOut { from { transform: scale(1.10);                     } to { transform: scale(1.0);                     } }
        @keyframes kbPanL    { from { transform: scale(1.06) translateX(0%);      } to { transform: scale(1.06) translateX(-3.5%);  } }
        @keyframes kbPanR    { from { transform: scale(1.06) translateX(-3.5%);   } to { transform: scale(1.06) translateX(0%);     } }
        @keyframes slideProgress { from { width: 0%; } to { width: 100%; } }
        @keyframes scrollBounce  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(7px); } }
        @keyframes scrollFadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bgFadeIn      { from { opacity: 0; } to   { opacity: 1; } }
        @keyframes heroCapIn     { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      {/* Background container, overflow hidden here to clip parallax images */}
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0, animation: "bgFadeIn 0.35s ease 0s both" }}>
        {/* Custom background media, only render the ACTIVE backgroundType */}
        {backgroundData?.backgroundType ? (
          <>
            {/* Image carousel, only when type is 'image' */}
            {backgroundData.backgroundType === 'image' && backgroundData.backgroundImages?.length > 0 && (
              <>
                {backgroundData.backgroundImages.map((img, i) => {
                  // Only render the active slide + the one currently fading out.
                  // All other slides stay unmounted so their images are never fetched.
                  if (i !== idx && i !== prevIdxRef.current) return null;
                  const kbAnim = ["kbZoomIn","kbZoomOut","kbPanL","kbPanR"][i % 4];
                  return (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        inset: "-10% 0",
                        opacity: i === idx ? 1 : 0,
                        transition: "opacity 2.5s ease",
                        transform: `translateY(${parallaxY}px)`,
                        willChange: "opacity, transform",
                      }}
                    >
                      <img
                        src={imgUrl(img.url, { width: 1920, quality: 72, resize: "contain" })}
                        srcSet={`${imgUrl(img.url, { width: 768, quality: 70, resize: "contain" })} 768w, ${imgUrl(img.url, { width: 1280, quality: 72, resize: "contain" })} 1280w, ${imgUrl(img.url, { width: 1920, quality: 72, resize: "contain" })} 1920w`}
                        sizes="100vw"
                        alt={img.alt || ""}
                        loading={i === idx ? "eager" : "lazy"}
                        fetchpriority={i === idx ? "high" : "auto"}
                        decoding="async"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          animationName: kbAnim,
                          animationDuration: "16s",
                          animationTimingFunction: "ease-in-out",
                          animationDirection: "alternate",
                          animationIterationCount: "infinite",
                          animationPlayState: i === idx ? "running" : "paused",
                        }}
                      />
                    </div>
                  );
                })}
              </>
            )}

            {/* Video carousel, only when type is 'video_upload' */}
            {backgroundData.backgroundType === 'video_upload' && backgroundData.backgroundVideos?.length > 0 && (
              <>
                {backgroundData.backgroundVideos.map((vid, i) => (
                  <div
                    key={vid.url}
                    style={{
                      position: "absolute",
                      inset: 0,
                      overflow: "hidden",
                      opacity: i === idx ? 1 : 0,
                      transition: "opacity 2s ease",
                    }}
                  >
                    {vid.isUrl ? (
                      <iframe
                        src={vid.url}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                        }}
                        allow="autoplay"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        autoPlay={backgroundData.autoplay !== false}
                        muted={backgroundData.muted !== false}
                        loop={backgroundData.loop !== false}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center center",
                        }}
                      >
                        <source src={vid.url} type={`video/${vid.type}`} />
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* YouTube, only when type is 'youtube', src deferred 1.2s so critical content paints first */}
            {backgroundData.backgroundType === 'youtube' && backgroundData.backgroundVideoUrl && extractYouTubeId(backgroundData.backgroundVideoUrl) && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                }}
              >
                <iframe
                  src={videoReady ? `https://www.youtube.com/embed/${extractYouTubeId(backgroundData.backgroundVideoUrl)}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&playlist=${extractYouTubeId(backgroundData.backgroundVideoUrl)}` : ''}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    border: "none",
                    pointerEvents: "none",
                  }}
                  allow="autoplay"
                  title="YouTube video background"
                />
              </div>
            )}

            {/* Vimeo, only when type is 'vimeo', src deferred 1.2s so critical content paints first */}
            {backgroundData.backgroundType === 'vimeo' && backgroundData.backgroundVideoUrl && extractVimeoId(backgroundData.backgroundVideoUrl) && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                }}
              >
                <iframe
                  src={videoReady ? `https://player.vimeo.com/video/${extractVimeoId(backgroundData.backgroundVideoUrl)}?autoplay=1&muted=1&loop=1&controls=0` : ''}
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    border: "none",
                    pointerEvents: "none",
                  }}
                  allow="autoplay"
                  title="Vimeo video background"
                />
              </div>
            )}
          </>
        ) : (() => {
            // 1. YouTube
            const ytId = extractYouTubeId(designSettings.homepage_hero_youtube || "");
            if (ytId) return (
              <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                <iframe
                  src={videoReady ? `https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&playlist=${ytId}` : ""}
                  style={{ position: "absolute", width: "177.78vh", minWidth: "100%", height: "56.25vw", minHeight: "100%", top: "50%", left: "50%", transform: "translate(-50%,-50%)", border: "none", pointerEvents: "none" }}
                  allow="autoplay"
                  title="Hero background"
                />
              </div>
            );

            // 2. Vimeo
            const vimeoId = extractVimeoId(designSettings.homepage_hero_vimeo || "");
            if (vimeoId) return (
              <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
                <iframe
                  src={videoReady ? `https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=1&loop=1&controls=0&background=1` : ""}
                  style={{ position: "absolute", width: "177.78vh", minWidth: "100%", height: "56.25vw", minHeight: "100%", top: "50%", left: "50%", transform: "translate(-50%,-50%)", border: "none", pointerEvents: "none" }}
                  allow="autoplay"
                  title="Hero background"
                />
              </div>
            );

            // 3. Image carousel, homepage_slider_image + default_hero_slider_1..4
            if (heroSliderImages.length > 0) {
              const KB_ANIMS = ["kbZoomIn","kbZoomOut","kbPanL","kbPanR"];
              return heroSliderImages.map((slide, i) => {
                if (i !== idx && i !== prevIdxRef.current) return null;
                const src = typeof slide === "string" ? slide : (slide?.url || "");
                return (
                  <div key={i} style={{ position: "absolute", inset: "-10% 0", opacity: i === idx ? 1 : 0, transition: "opacity 2.5s ease", transform: `translateY(${parallaxY}px)`, willChange: "opacity, transform" }}>
                    <img
                      src={imgUrl(src, { width: 2000, quality: 86, resize: "contain" })}
                      srcSet={`${imgUrl(src, { width: 828, quality: 82, resize: "contain" })} 828w, ${imgUrl(src, { width: 1280, quality: 85, resize: "contain" })} 1280w, ${imgUrl(src, { width: 1920, quality: 86, resize: "contain" })} 1920w, ${imgUrl(src, { width: 2560, quality: 84, resize: "contain" })} 2560w`}
                      sizes="100vw" alt="" loading={i === idx ? "eager" : "lazy"} decoding="async"
                      fetchpriority={i === idx ? "high" : "auto"}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", filter: "contrast(1.06) saturate(1.16) brightness(1.03)", animationName: KB_ANIMS[i % 4], animationDuration: "16s", animationTimingFunction: "ease-in-out", animationDirection: "alternate", animationIterationCount: "infinite", animationPlayState: i === idx ? "running" : "paused" }}
                    />
                  </div>
                );
              });
            }

            return null;
          })()
        }

        {/* Cinematic overlays, three layers for depth */}
        {/* Layer 1: top & bottom gradient fog */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background:
              "linear-gradient(to bottom, rgba(4,3,2,0.65) 0%, rgba(4,3,2,0.1) 30%, rgba(4,3,2,0.1) 55%, rgba(4,3,2,0.82) 85%, rgba(4,3,2,1) 100%)",
          }}
        />
        {/* Layer 2: edge vignette */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background:
              "radial-gradient(ellipse 90% 70% at 50% 42%, transparent 20%, rgba(4,3,2,0.5) 100%)",
          }}
        />
        {/* Layer 3: subtle warm colour grade */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            background: "rgba(40,24,8,0.05)",
            mixBlendMode: "multiply",
          }}
        />
      </div>

      {/* Hero credit, right edge, rotated, faded */}
      {designSettings.homepage_hero_credit && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 0,
            top: "50%",
            transform: "translateY(-50%) rotate(90deg)",
            transformOrigin: "center center",
            zIndex: 3,
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{
            fontFamily: NU,
            fontSize: 9,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.28)",
            fontWeight: 400,
          }}>
            {designSettings.homepage_hero_credit}
          </span>
        </div>
      )}

      {/* Active hero slide, venue name, bottom-right */}
      {(() => {
        const active = heroSliderImages[idx];
        const name = active && typeof active === "object" ? (active.name || "") : "";
        const loc = active && typeof active === "object" ? (active.location || "") : "";
        if (!name) return null;
        return (
          <div key={`hcap-${idx}`} style={{ position: "absolute", right: "clamp(18px,3vw,46px)", bottom: "clamp(16px,3.5vh,36px)", zIndex: 3, textAlign: "right", pointerEvents: "none", animation: "heroCapIn 900ms ease" }}>
            <div style={{ fontFamily: GD, fontSize: "clamp(14px,1.5vw,18px)", lineHeight: 1.2, color: "rgba(255,255,255,0.92)", letterSpacing: "0.01em", textShadow: "0 1px 10px rgba(0,0,0,0.55)" }}>{name}</div>
            {loc && <div style={{ fontFamily: NU, fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)", marginTop: 4 }}>{loc}</div>}
          </div>
        );
      })()}

      {/* Content */}
      <div
        className="home-hero-content"
        style={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          padding: "0 24px",
          maxWidth: 860,
          width: "100%",
          marginBottom: "12.5vh",
          animation: "fadeUp 0.8s ease both",
        }}
      >
        {/* Optional eyebrow above the headline */}
        {eyebrow && (
          <div
            className="home-hero-badge"
            style={{
              fontFamily: NU,
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(211,154,33,0.85)",
              marginBottom: 22,
            }}
          >
            {eyebrow}
          </div>
        )}

        {/* Headline, one phrase, word-by-word reveal (wraps gracefully on mobile).
            ONE <h1>: the two lines are <span>s so each keeps its own colour, italic
            and SplitText delay. The {" "} between them is a whitespace-only text node,
            which flex layout ignores, but it makes textContent read "Line1 Line2"
            rather than welding the words together for crawlers and screen readers. */}
        <h1 style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "baseline", columnGap: "0.32em", rowGap: 0, margin: 0, marginBottom: 16, fontWeight: 400 }}>
          <span
            style={{
              fontFamily: GD,
              fontSize: "clamp(28px, 4vw, 54px)",
              fontWeight: 400,
              color: "#f5f0e8",
              lineHeight: 1.05,
              letterSpacing: "-0.5px",
            }}
          >
            <SplitText trigger={heroLoaded} delay={100} stagger={100}>
              {headlineLine1 || "The World's Finest"}
            </SplitText>
          </span>{" "}
          <span
            style={{
              fontFamily: GD,
              fontSize: "clamp(28px, 4vw, 54px)",
              fontWeight: 400,
              fontStyle: "italic",
              color: "#d39a21",
              lineHeight: 1.05,
              letterSpacing: "-0.5px",
            }}
          >
            <SplitText trigger={heroLoaded} delay={350} stagger={100}>
              {headlineLine2 || "Wedding Directory"}
            </SplitText>
          </span>
        </h1>

        {/* Support line, heritage beneath the category claim */}
        {supportLine ? (
          <div style={{ fontFamily: NU, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.85)", marginTop: 16, marginBottom: 30 }}>
            {supportLine}
          </div>
        ) : (
          <div style={{ height: 30 }} />
        )}

        {/* One discovery command bar, no AI/Browse toggle, no decision asked */}
        <div ref={searchBarRef} style={{ position: "relative", width: "100%", maxWidth: 720, margin: "0 auto", animation: "fadeUp 0.8s ease 0.3s both" }}>
          <DiscoveryBar
            containerRef={searchBarRef}
            onViewVenue={onViewVenue}
            onViewRegion={onViewRegion}
            onViewEditArticle={onViewEditArticle}
            onViewAtlas={onViewAtlas}
            onViewPublication={onViewPublication}
            onViewRealWedding={onViewRealWedding}
            onViewSearch={onViewSearch}
            onOpenImmersive={onOpenImmersive}
          />
        </div>

        {/* Powered by Aura, invisible infrastructure */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginTop: 24, fontFamily: NU, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(245,240,232,0.34)" }}>
          <span aria-hidden="true" style={{ color: "rgba(211,154,33,0.6)", fontSize: 12 }}>✦</span>
          Powered by Aura intelligence
        </div>

        {/* Trust micro-line */}
        <div
          className="home-hero-trust"
          style={{
            marginTop: 12,
            fontSize: 11,
            color: "rgba(255,255,255,0.25)",
            letterSpacing: "0.14em",
            fontFamily: NU,
          }}
        >
          Every listing individually reviewed · Curated, never crowdsourced
        </div>
      </div>


      {/* ── Scroll indicator ─────────────────────────────────────────────────── */}
      <div
        className="home-hero-scroll-cue"
        style={{
          position: "absolute",
          bottom: "calc(28px + env(safe-area-inset-bottom, 0px))",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          animation: "scrollFadeIn 1s ease 2.5s both",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: 1,
            height: 40,
            background: "linear-gradient(to bottom, rgba(211,154,33,0.55), transparent)",
            animation: "scrollBounce 2.2s ease-in-out 2.5s infinite",
          }}
        />
      </div>
    </section>
  );
}
